import { Button, Textarea, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Api } from '@/shared/api';
import { RouteList } from '@/shared/constants';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';

import { useUpdateAuthorMutation } from '../model/edit-author-page.cases';
import { EDIT_AUTHOR_SCHEMA } from '../model/edit-author-page.schemas';
import styles from './edit-author-page.module.scss';

export const EditAuthorPage = () => {
  const form = useForm({
    initialValues: {
      name: '',
      description: '',
      birthday: '',
    },
    validate: zodResolver(EDIT_AUTHOR_SCHEMA),
  });
  const params = useParams();
  const project = useCurrentProject();
  const withProject = useProjectPath();
  useDirtyForm(form.isDirty());

  const getAuthorQuery = useQuery({
    queryKey: [project, 'author', params.authorId],
    queryFn: () => Api.authors.getAuthor(project, Number(params.authorId)),
    enabled: !!params.authorId,
  });
  const updateAuthorMutation = useUpdateAuthorMutation();
  const navigate = useNavigate();

  async function updateAuthor(data: {
    name: string;
    description: string;
    birthday: string;
  }) {
    await updateAuthorMutation
      .mutateAsync({
        id: Number(params.authorId),
        name: data.name,
        description: data.description,
        birthday: data.birthday || undefined,
      })
      .then(() => {
        notifications.show({
          message: 'Автор обновлен',
          color: 'green',
        });
        return navigate(
          withProject(RouteList.AUTHOR_DETAILS(String(params.authorId))),
          {
            replace: true,
          },
        );
      })
      .catch(() => {
        notifications.show({
          message: 'Не удалось обновить автора',
          color: 'red',
        });
      });
  }

  useEffect(() => {
    if (getAuthorQuery.data) {
      form.setValues({
        name: getAuthorQuery.data.name,
        description: getAuthorQuery.data.description || '',
        birthday: getAuthorQuery.data.birthday || '',
      });
      form.resetDirty();
    }
  }, [getAuthorQuery.data]);

  return (
    <form className={styles.container} onSubmit={form.onSubmit(updateAuthor)}>
      <p className={styles.title}>Редактирование автора</p>
      <TextInput
        placeholder="Имя автора"
        label="Имя"
        className={styles.nameInput}
        {...form.getInputProps('name')}
      />
      <Textarea
        placeholder="Описание автора"
        label="Описание"
        className={styles.descriptionInput}
        {...form.getInputProps('description')}
      />
      {project === 'kitaba' && (
        <TextInput
          type="date"
          label="Дата рождения"
          {...form.getInputProps('birthday')}
        />
      )}
      <div className={styles.actions}>
        <Button type="submit">Сохранить</Button>
        <Button variant="light" onClick={() => history.back()}>
          Отменить
        </Button>
      </div>
    </form>
  );
};
